import React, { useState, useEffect } from 'react';
import DashboardLayout from './DashboardLayout';
import { getAppointments, getTodayOrders, updateAppointment, updateOrderStatus, getOperators, createAppointmentAdmin, getServices } from '../lib/api';
import { apiClient } from '../lib/api';
import TicketModal from '../components/pos/TicketModal';
import { isBluetoothSupported, printTicketBluetooth } from '../lib/bluetoothPrinter';
import { formatCurrency, getStatusLabel, getVehicleTypeLabel, formatTime } from '../lib/utils';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Calendar as CalendarIcon, Clock, Car, User, Phone, RefreshCw, Loader2,
  CheckCircle, XCircle, ChevronLeft, ChevronRight, Plus, MapPin, MessageCircle, Printer
} from 'lucide-react';
import { toast } from 'sonner';
import { format, addDays, subDays } from 'date-fns';
import { es } from 'date-fns/locale';

const TIME_SLOTS = [];
for (let h = 8; h <= 15; h++) {
  for (let m of [0, 30]) {
    if (h === 8 && m === 0) continue; // Start at 8:30
    if (h === 15 && m > 30) continue; // End at 15:30
    TIME_SLOTS.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
  }
}

export default function AgendaPage() {
  const { isAdmin, isCajero, isOperador } = useAuth();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [appointments, setAppointments] = useState([]);
  const [orders, setOrders] = useState([]);
  const [operators, setOperators] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('agenda');

  // Detail modal
  const [selectedItem, setSelectedItem] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [operatorTicket, setOperatorTicket] = useState(null);
  const [showOperatorTicket, setShowOperatorTicket] = useState(false);

  // New appointment modal (admin)
  const [showNewModal, setShowNewModal] = useState(false);
  const [newAppt, setNewAppt] = useState({
    name: '', phone: '', vehicle_type: 'sedan', service_id: '',
    operator_id: '', time: '', service_type: 'centro_lavado', notes: '', address: '', google_maps_link: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { fetchData(); }, [selectedDate]);

  const fetchData = async () => {
    setLoading(true);
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    try {
      const promises = [
        getAppointments({ date: dateStr }),
        getTodayOrders()
      ];
      if (isAdmin || isCajero) {
        promises.push(getOperators());
        promises.push(getServices());
      }
      const results = await Promise.all(promises);
      setAppointments(results[0].data);
      setOrders(results[1].data.filter(o => o.date === dateStr));
      if ((isAdmin || isCajero) && results[2]) setOperators(results[2].data);
      if ((isAdmin || isCajero) && results[3]) setServices(results[3].data);
    } catch (error) {

    }
    setLoading(false);
  };

  const handleConfirm = async (id) => {
    try {
      const res = await updateAppointment(id, { status: 'confirmado' });
      const newOrderId = res.data?.order_id || null;
      setAppointments(prev => prev.map(a => a.id === id ? { ...a, status: 'confirmado', order_id: newOrderId || a.order_id } : a));
      if (res.data?.auto_created_order) {
        toast.success('Cita confirmada · orden generada para el operador', { duration: 4000 });
      } else {
        toast.success('Cita confirmada');
      }
    } catch (error) { toast.error('Error al confirmar'); }
  };

  const handleCancel = async (id) => {
    try {
      await updateAppointment(id, { status: 'cancelado' });
      setAppointments(prev => prev.map(a => a.id === id ? { ...a, status: 'cancelado' } : a));
      toast.success('Cita cancelada');
    } catch (error) { toast.error('Error al cancelar'); }
  };

  const handleUpdateStatus = async (id, newStatus) => {
    try {
      const res = await updateAppointment(id, { status: newStatus });
      const newOrderId = res.data?.order_id || null;
      setAppointments(prev => prev.map(a => a.id === id ? { ...a, status: newStatus, order_id: newOrderId || a.order_id } : a));
      if (res.data?.auto_created_order) {
        toast.success('Orden generada automáticamente para el operador', { duration: 4000 });
      } else {
        toast.success('Estado actualizado');
      }
      return res.data;
    } catch (error) {
      toast.error('Error al actualizar');
      return null;
    }
  };

  const handleOpenOperatorTicket = async (apt) => {
    try {
      let order = null;
      if (apt.order_id) {
        const res = await apiClient.get(`/orders/${apt.order_id}`);
        order = res.data;
      }
      const folio = order?.folio || `APT-${(apt.id || '').slice(-6).toUpperCase()}`;
      const services = order?.services || [
        apt.service ? { name: apt.service.name, price: apt.service.price } : null,
        ...(apt.additional_units || []).map((u) => ({ name: u.service_name, price: u.service_price })),
      ].filter(Boolean);
      if (apt.delivery_fee > 0 && !order) {
        services.push({
          name: apt.service_type === 'domicilio' ? 'Cargo a domicilio' : 'Cargo por recogida y entrega',
          price: apt.delivery_fee,
        });
      }
      const total = services.reduce((sum, s) => sum + Number(s.price || 0), 0);
      setOperatorTicket({
        folio,
        date: new Date().toLocaleString('es-MX'),
        client: { name: apt.name, phone: apt.phone },
        walk_in: !apt.client_id,
        vehicle: { type: apt.vehicle_type, model: null, plates: null },
        services,
        total,
        payment_method: null,
        points_earned: 0,
        points_redeemed: 0,
        is_operator_ticket: true,
        address: apt.address,
        appointment_time: apt.time,
        appointment_date: apt.date,
      });
      setShowOperatorTicket(true);
    } catch (err) {
      toast.error('No se pudo cargar el ticket');
    }
  };

  const handleUpdateOrderStatus = async (id, newStatus) => {
    try {
      await updateOrderStatus(id, newStatus);
      setOrders(prev => prev.map(o => o.id === id ? { ...o, service_status: newStatus } : o));
      toast.success('Estado actualizado');
    } catch (error) { toast.error('Error al actualizar'); }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await createAppointmentAdmin({
        ...newAppt,
        date: format(selectedDate, 'yyyy-MM-dd'),
        address: newAppt.address || null,
        google_maps_link: newAppt.google_maps_link || null,
        notes: newAppt.notes || null
      });
      toast.success('Cita creada y confirmada');
      setShowNewModal(false);
      setNewAppt({ name: '', phone: '', vehicle_type: 'sedan', service_id: '', operator_id: '', time: '', service_type: 'centro_lavado', notes: '', address: '', google_maps_link: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear cita');
    }
    setSubmitting(false);
  };

  const navigateDate = (direction) => {
    setSelectedDate(direction === 'next' ? addDays(selectedDate, 1) : subDays(selectedDate, 1));
  };

  const getStatusColor = (status) => {
    const colors = {
      pendiente: 'bg-amber-100 border-amber-300 text-amber-800',
      confirmado: 'bg-blue-100 border-blue-300 text-blue-800',
      en_proceso: 'bg-indigo-100 border-indigo-300 text-indigo-800',
      listo: 'bg-emerald-100 border-emerald-300 text-emerald-800',
      cancelado: 'bg-slate-100 border-slate-300 text-slate-500 opacity-50',
    };
    return colors[status] || 'bg-slate-100 border-slate-200';
  };

  const getWhatsAppUrl = (phone) => {
    if (!phone) return '#';
    const digits = phone.replace(/\D/g, '');
    const mxPhone = digits.length === 10 ? `52${digits}` : digits;
    return `https://wa.me/${mxPhone}`;
  };

  const getAppointmentWhatsAppUrl = (apt) => {
    if (!apt?.phone) return '#';
    const digits = apt.phone.replace(/\D/g, '');
    const mxPhone = digits.length === 10 ? `52${digits}` : digits;
    const dateStr = apt.date
      ? format(new Date(`${apt.date}T00:00:00`), "EEEE d 'de' MMMM", { locale: es })
      : '';
    const timeInfo = apt.time
      ? ` a las ${formatTime(apt.time)}`
      : apt.time_block
        ? ` (${apt.time_block === 'manana' ? 'turno de mañana' : 'turno de tarde'})`
        : '';
    const unitsInfo = apt.units > 1 ? ` para ${apt.units} unidades` : '';
    const parts = [
      `Hola ${apt.name || ''}, te escribo de WD Lavado Automotriz sobre tu cita del ${dateStr}${timeInfo}${unitsInfo}.`,
    ];
    if (apt.service?.name) parts.push(`Servicio: ${apt.service.name}.`);
    if (apt.service_type && apt.service_type !== 'centro_lavado') {
      parts.push(
        apt.service_type === 'domicilio'
          ? 'Servicio a domicilio — te confirmo el cargo extra por distancia.'
          : 'Recogida y entrega — te confirmo el cargo extra por distancia.',
      );
    }
    const message = encodeURIComponent(parts.join(' '));
    return `https://wa.me/${mxPhone}?text=${message}`;
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  const pendingCount = appointments.filter(a => a.status === 'pendiente').length;

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fadeIn">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Agenda Operativa</h1>
            <p className="text-slate-600 mt-1">
              {isOperador ? 'Tu agenda de servicios' : 'Gestiona citas y servicios por operador'}
            </p>
          </div>
          <div className="flex gap-2">
            {(isAdmin || isCajero) && (
              <Button onClick={() => setShowNewModal(true)} className="gap-2" data-testid="new-appointment-btn">
                <Plus className="w-4 h-4" />
                Agendar Manual
              </Button>
            )}
            <Button onClick={fetchData} variant="outline" className="gap-2">
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Date Navigation */}
        <div className="card-base p-4">
          <div className="flex items-center justify-between">
            <Button variant="outline" size="icon" onClick={() => navigateDate('prev')} data-testid="prev-date-btn">
              <ChevronLeft className="w-5 h-5" />
            </Button>
            <div className="text-center">
              <div className="text-2xl font-bold text-slate-900">
                {format(selectedDate, "EEEE d 'de' MMMM", { locale: es })}
              </div>
            </div>
            <Button variant="outline" size="icon" onClick={() => navigateDate('next')} data-testid="next-date-btn">
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Pending Confirmations Alert */}
        {isAdmin && pendingCount > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-center gap-3" data-testid="pending-alert">
            <CalendarIcon className="w-5 h-5 text-amber-600 shrink-0" />
            <span className="text-amber-800 font-medium">{pendingCount} cita(s) pendiente(s) de confirmación</span>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-2 max-w-md">
            <TabsTrigger value="agenda" data-testid="tab-agenda">
              <CalendarIcon className="w-4 h-4 mr-2" />
              Agenda {pendingCount > 0 && `(${pendingCount})`}
            </TabsTrigger>
            <TabsTrigger value="ordenes" data-testid="tab-ordenes">
              <Car className="w-4 h-4 mr-2" />
              En Proceso ({orders.filter(o => o.service_status !== 'entregado').length})
            </TabsTrigger>
          </TabsList>

          {/* Agenda by Operator */}
          <TabsContent value="agenda" className="mt-6">
            {isAdmin && operators.length > 0 ? (
              <div className="card-base overflow-x-auto">
                <table className="w-full border-collapse min-w-[800px]">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="p-3 text-left text-sm font-semibold text-slate-600 w-20 bg-slate-50 sticky left-0">Hora</th>
                      {operators.map(op => (
                        <th key={op.id} className="p-3 text-center text-sm font-semibold text-slate-700 min-w-[180px]" data-testid={`op-col-${op.id}`}>
                          <div className="flex items-center justify-center gap-2">
                            <User className="w-4 h-4 text-blue-500" />
                            {op.name}
                          </div>
                          {op.rest_day && (
                            <div className="text-xs font-normal text-slate-400 mt-0.5">Desc: {op.rest_day}</div>
                          )}
                        </th>
                      ))}
                      <th className="p-3 text-center text-sm font-semibold text-slate-500 min-w-[160px]">Sin asignar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {TIME_SLOTS.map(time => (
                      <tr key={time} className="border-b border-slate-100 hover:bg-slate-50/50">
                        <td className="p-2 text-sm font-medium text-slate-500 bg-slate-50 sticky left-0">{formatTime(time)}</td>
                        {operators.map(op => {
                          const apt = appointments.find(a => a.time === time && a.operator_id === op.id && a.status !== 'cancelado');
                          return (
                            <td key={op.id} className="p-1">
                              {apt ? (
                                <button
                                  className={`w-full p-2 rounded-lg border text-left text-xs transition-all ${getStatusColor(apt.status)}`}
                                  onClick={() => { setSelectedItem(apt); setShowDetailModal(true); }}
                                  data-testid={`apt-cell-${apt.id}`}
                                >
                                  <div className="font-semibold truncate">{apt.name}</div>
                                  <div className="truncate opacity-80">{apt.service?.name}</div>
                                  <div className="flex gap-1 mt-1">
                                    <a href={getWhatsAppUrl(apt.phone)} target="_blank" rel="noopener noreferrer" className="p-0.5 rounded bg-green-500 text-white" onClick={(e) => e.stopPropagation()} data-testid={`whatsapp-${apt.id}`}>
                                      <MessageCircle className="w-3.5 h-3.5" />
                                    </a>
                                    {apt.status === 'pendiente' && isAdmin && (
                                      <>
                                        <button className="p-0.5 rounded bg-emerald-500 text-white" onClick={(e) => { e.stopPropagation(); handleConfirm(apt.id); }} data-testid={`confirm-${apt.id}`}>
                                          <CheckCircle className="w-3.5 h-3.5" />
                                        </button>
                                        <button className="p-0.5 rounded bg-red-500 text-white" onClick={(e) => { e.stopPropagation(); handleCancel(apt.id); }} data-testid={`cancel-${apt.id}`}>
                                          <XCircle className="w-3.5 h-3.5" />
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </button>
                              ) : (
                                <div className="h-10 rounded border border-dashed border-slate-200" />
                              )}
                            </td>
                          );
                        })}
                        {/* Unassigned column */}
                        <td className="p-1">
                          {(() => {
                            const unassigned = appointments.filter(a => a.time === time && !a.operator_id && a.status !== 'cancelado');
                            return unassigned.map(apt => (
                              <button
                                key={apt.id}
                                className={`w-full p-2 rounded-lg border text-left text-xs mb-1 ${getStatusColor(apt.status)}`}
                                onClick={() => { setSelectedItem(apt); setShowDetailModal(true); }}
                              >
                                <div className="font-semibold truncate">{apt.name}</div>
                                <div className="truncate opacity-80">{apt.service?.name}</div>
                              </button>
                            ));
                          })()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              /* Operator or no-operator view: simple list */
              <div className="space-y-3">
                {appointments.length === 0 ? (
                  <div className="card-base p-12 text-center text-slate-400">
                    <CalendarIcon className="w-10 h-10 mx-auto mb-2 opacity-50" />
                    <p>No hay citas para esta fecha</p>
                  </div>
                ) : appointments.map(apt => (
                  <div key={apt.id} className={`card-base p-4 border-l-4 ${
                    apt.status === 'pendiente' ? 'border-l-amber-500' :
                    apt.status === 'confirmado' ? 'border-l-blue-500' :
                    apt.status === 'en_proceso' ? 'border-l-indigo-500' :
                    apt.status === 'listo' ? 'border-l-emerald-500' : 'border-l-slate-300'
                  }`} data-testid={`apt-card-${apt.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 text-slate-400" />
                        <span className="font-medium">{formatTime(apt.time)}</span>
                        <span className="text-slate-500">—</span>
                        <span className="font-semibold text-slate-900">{apt.name}</span>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium border ${getStatusColor(apt.status)}`}>
                        {getStatusLabel(apt.status)}
                      </span>
                    </div>
                    <div className="text-sm text-slate-600">
                      {apt.service?.name} · {getVehicleTypeLabel(apt.vehicle_type)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Orders View */}
          <TabsContent value="ordenes" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {orders.length === 0 ? (
                <div className="col-span-full text-center py-12 text-slate-400">
                  <Car className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No hay órdenes para esta fecha</p>
                </div>
              ) : orders.map((order) => (
                <div key={order.id} className={`card-base p-4 ${
                  order.service_status === 'recibido' ? 'border-l-4 border-l-amber-500' :
                  order.service_status === 'en_proceso' ? 'border-l-4 border-l-blue-500' :
                  order.service_status === 'listo' ? 'border-l-4 border-l-emerald-500' :
                  'border-l-4 border-l-slate-300'
                }`} data-testid={`order-card-${order.id}`}>
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="font-mono text-blue-600 font-semibold">{order.folio}</div>
                      <div className="font-medium text-slate-900 mt-1">{order.client?.name}</div>
                    </div>
                    <span className={`status-badge status-${order.service_status}`}>
                      {getStatusLabel(order.service_status)}
                    </span>
                  </div>
                  <div className="flex flex-wrap gap-1 mb-3">
                    {order.services?.map((s, i) => (
                      <span key={i} className="px-2 py-1 bg-slate-100 rounded text-xs font-medium">{s.name}</span>
                    ))}
                  </div>
                  <Select value={order.service_status} onValueChange={(value) => handleUpdateOrderStatus(order.id, value)}>
                    <SelectTrigger data-testid={`order-status-${order.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="recibido">Recibido</SelectItem>
                      <SelectItem value="en_proceso">En Proceso</SelectItem>
                      <SelectItem value="listo">Listo</SelectItem>
                      <SelectItem value="entregado">Entregado</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        {/* Summary */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {[
            { label: 'Pendientes', count: appointments.filter(a => a.status === 'pendiente').length, color: 'bg-amber-50 text-amber-600 border-amber-200' },
            { label: 'Confirmadas', count: appointments.filter(a => a.status === 'confirmado').length, color: 'bg-blue-50 text-blue-600 border-blue-200' },
            { label: 'En Proceso', count: appointments.filter(a => a.status === 'en_proceso').length, color: 'bg-indigo-50 text-indigo-600 border-indigo-200' },
            { label: 'Listos', count: appointments.filter(a => a.status === 'listo').length, color: 'bg-emerald-50 text-emerald-600 border-emerald-200' },
            { label: 'Cancelados', count: appointments.filter(a => a.status === 'cancelado').length, color: 'bg-slate-50 text-slate-500 border-slate-200' },
          ].map(s => (
            <div key={s.label} className={`p-3 rounded-xl border ${s.color}`}>
              <div className="text-2xl font-bold">{s.count}</div>
              <div className="text-xs font-medium">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Appointment Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalle de Cita</DialogTitle>
          </DialogHeader>
          {selectedItem && (
            <div className="space-y-4">
              {/* Client card with prominent WhatsApp CTA */}
              <div className="p-4 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-xs text-blue-600 font-semibold uppercase tracking-wide">Cliente</div>
                    <div className="text-lg font-bold text-slate-900 truncate">{selectedItem.name}</div>
                    <div className="flex items-center gap-1 text-sm text-slate-600 mt-1">
                      <Phone className="w-3.5 h-3.5" />
                      <span className="font-mono">{selectedItem.phone}</span>
                    </div>
                  </div>
                  <span className={`status-badge status-${selectedItem.status} shrink-0`}>
                    {getStatusLabel(selectedItem.status)}
                  </span>
                </div>
                <a
                  href={getAppointmentWhatsAppUrl(selectedItem)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-green-500 hover:bg-green-600 text-white font-semibold rounded-lg transition-colors"
                  data-testid="detail-whatsapp-btn"
                >
                  <MessageCircle className="w-4 h-4" />
                  Enviar WhatsApp con detalles de la cita
                </a>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-xs text-slate-500">Vehículo</div>
                  <div className="font-medium">{getVehicleTypeLabel(selectedItem.vehicle_type)}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Servicio</div>
                  <div className="font-medium">{selectedItem.service?.name || '—'}</div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Hora</div>
                  <div className="font-medium">
                    {selectedItem.time
                      ? formatTime(selectedItem.time)
                      : selectedItem.time_block === 'manana'
                        ? 'Mañana'
                        : selectedItem.time_block === 'tarde'
                          ? 'Tarde'
                          : '—'}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-slate-500">Operador</div>
                  <div className="font-medium">{selectedItem.operator?.name || 'Sin asignar'}</div>
                </div>
                {selectedItem.units > 1 && (
                  <div>
                    <div className="text-xs text-slate-500">Unidades</div>
                    <div className="font-medium">{selectedItem.units}</div>
                  </div>
                )}
                {selectedItem.branch_name && (
                  <div>
                    <div className="text-xs text-slate-500">Sucursal</div>
                    <div className="font-medium">{selectedItem.branch_name}</div>
                  </div>
                )}
              </div>

              {/* Additional units details */}
              {Array.isArray(selectedItem.additional_units) && selectedItem.additional_units.length > 0 && (
                <div className="space-y-2">
                  <div className="text-xs text-slate-500 font-medium uppercase">Unidades adicionales</div>
                  {selectedItem.additional_units.map((u, idx) => (
                    <div key={idx} className="text-sm p-2 bg-slate-50 rounded-lg flex justify-between">
                      <span className="text-slate-700">
                        #{idx + 2} · {getVehicleTypeLabel(u.vehicle_type)}
                      </span>
                      <span className="font-medium text-slate-900">{u.service_name}</span>
                    </div>
                  ))}
                </div>
              )}

              {selectedItem.service_type !== 'centro_lavado' && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <div className="text-xs text-amber-700 font-medium mb-1">
                    {selectedItem.service_type === 'domicilio' ? 'Servicio a domicilio' : 'Recogida y entrega'}
                    {selectedItem.delivery_fee > 0 && ` · cargo ${formatCurrency(selectedItem.delivery_fee)}`}
                  </div>
                  {selectedItem.address && (
                    <div className="flex items-start gap-1 text-sm text-slate-700">
                      <MapPin className="w-3 h-3 mt-0.5 shrink-0" />
                      <span>{selectedItem.address}</span>
                    </div>
                  )}
                  {selectedItem.google_maps_link && (
                    <a
                      href={selectedItem.google_maps_link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-blue-600 underline"
                    >
                      Ver en Google Maps
                    </a>
                  )}
                  <button
                    type="button"
                    onClick={() => handleOpenOperatorTicket(selectedItem)}
                    className="mt-3 w-full inline-flex items-center justify-center gap-2 px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-lg"
                    data-testid="operator-ticket-btn"
                  >
                    <Printer className="w-4 h-4" />
                    Imprimir ticket del operador
                    {selectedItem.order_id && <span className="ml-1 text-xs text-emerald-300">· orden ya generada</span>}
                  </button>
                </div>
              )}

              {selectedItem.notes && (
                <div>
                  <div className="text-xs text-slate-500">Notas</div>
                  <div className="text-sm text-slate-700">{selectedItem.notes}</div>
                </div>
              )}

              {isAdmin && (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <Label className="mb-2 block">Asignar operador</Label>
                    <Select
                      value={selectedItem.operator_id || 'unassigned'}
                      onValueChange={async (value) => {
                        const newOpId = value === 'unassigned' ? null : value;
                        try {
                          await updateAppointment(selectedItem.id, { operator_id: newOpId });
                          const opName = newOpId
                            ? (operators.find(o => o.id === newOpId)?.name || 'Operador')
                            : null;
                          setSelectedItem({
                            ...selectedItem,
                            operator_id: newOpId,
                            operator: newOpId ? { id: newOpId, name: opName } : null,
                          });
                          setAppointments(prev => prev.map(a =>
                            a.id === selectedItem.id
                              ? { ...a, operator_id: newOpId, operator: newOpId ? { id: newOpId, name: opName } : null }
                              : a
                          ));
                          toast.success(newOpId ? 'Operador asignado' : 'Operador removido');
                        } catch (e) {
                          toast.error(e?.response?.data?.detail || 'No se pudo asignar el operador');
                        }
                      }}
                    >
                      <SelectTrigger data-testid="detail-operator-select"><SelectValue placeholder="Sin asignar" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="unassigned">Sin asignar</SelectItem>
                        {operators.map(op => (
                          <SelectItem key={op.id} value={op.id} data-testid={`detail-operator-option-${op.id}`}>
                            {op.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="mb-2 block">Cambiar Estado</Label>
                    <Select value={selectedItem.status} onValueChange={(value) => {
                      handleUpdateStatus(selectedItem.id, value);
                      setSelectedItem({ ...selectedItem, status: value });
                    }}>
                      <SelectTrigger data-testid="detail-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pendiente">Pendiente</SelectItem>
                        <SelectItem value="confirmado">Confirmado</SelectItem>
                        <SelectItem value="en_proceso">En Proceso</SelectItem>
                        <SelectItem value="listo">Listo</SelectItem>
                        <SelectItem value="cancelado">Cancelado</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Admin New Appointment Modal */}
      <Dialog open={showNewModal} onOpenChange={setShowNewModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Agendar Manual - {format(selectedDate, "d MMM yyyy", { locale: es })}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreateAdmin} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Nombre *</Label>
                <Input value={newAppt.name} onChange={e => setNewAppt({...newAppt, name: e.target.value})} required className="mt-1" data-testid="admin-appt-name" />
              </div>
              <div>
                <Label>Teléfono *</Label>
                <Input value={newAppt.phone} onChange={e => setNewAppt({...newAppt, phone: e.target.value})} required className="mt-1" data-testid="admin-appt-phone" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Operador *</Label>
                <Select value={newAppt.operator_id} onValueChange={v => setNewAppt({...newAppt, operator_id: v})}>
                  <SelectTrigger className="mt-1" data-testid="admin-appt-operator"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {operators.map(op => <SelectItem key={op.id} value={op.id}>{op.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Horario *</Label>
                <Select value={newAppt.time} onValueChange={v => setNewAppt({...newAppt, time: v})}>
                  <SelectTrigger className="mt-1" data-testid="admin-appt-time"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {TIME_SLOTS.map(t => <SelectItem key={t} value={t}>{formatTime(t)}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Servicio *</Label>
                <Select value={newAppt.service_id} onValueChange={v => setNewAppt({...newAppt, service_id: v})}>
                  <SelectTrigger className="mt-1" data-testid="admin-appt-service"><SelectValue placeholder="Seleccionar..." /></SelectTrigger>
                  <SelectContent>
                    {services.filter(s => s.active !== false).map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name} - {formatCurrency(s.price)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Vehículo</Label>
                <Select value={newAppt.vehicle_type} onValueChange={v => setNewAppt({...newAppt, vehicle_type: v})}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="sedan">Sedán</SelectItem>
                    <SelectItem value="suv">SUV</SelectItem>
                    <SelectItem value="pickup">Pickup</SelectItem>
                    <SelectItem value="motocicleta">Motocicleta</SelectItem>
                    <SelectItem value="otro">Otro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Tipo de servicio</Label>
              <Select value={newAppt.service_type} onValueChange={v => setNewAppt({...newAppt, service_type: v})}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="centro_lavado">Centro de lavado</SelectItem>
                  <SelectItem value="domicilio">Servicio a domicilio</SelectItem>
                  <SelectItem value="recogida_entrega">Recogida y entrega</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {(newAppt.service_type === 'domicilio' || newAppt.service_type === 'recogida_entrega') && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Dirección</Label>
                  <Input value={newAppt.address} onChange={e => setNewAppt({...newAppt, address: e.target.value})} className="mt-1" placeholder="Calle y número..." />
                </div>
                <div>
                  <Label>Link Google Maps</Label>
                  <Input value={newAppt.google_maps_link} onChange={e => setNewAppt({...newAppt, google_maps_link: e.target.value})} className="mt-1" placeholder="https://maps.google.com/..." />
                </div>
              </div>
            )}
            <div>
              <Label>Notas</Label>
              <Input value={newAppt.notes} onChange={e => setNewAppt({...newAppt, notes: e.target.value})} className="mt-1" placeholder="Notas adicionales..." />
            </div>
            <Button type="submit" className="w-full" disabled={submitting || !newAppt.operator_id || !newAppt.time || !newAppt.service_id} data-testid="submit-admin-appt">
              {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Crear Cita Confirmada'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Operator Ticket Modal (reuses POS TicketModal for print/bluetooth) */}
      <TicketModal
        open={showOperatorTicket}
        onOpenChange={setShowOperatorTicket}
        ticketData={operatorTicket}
      />
    </DashboardLayout>
  );
}
